import React from 'react';
import PropTypes from 'prop-types';
import { formatThousands } from 'helpers/number';
import { formatDate } from 'helpers/utils';

const SOURCE_LABEL = { contract: 'Contract', vcb: 'Vietcombank', usd: 'USD' };

const RateCell = ({ rate }) => {
  if (!rate?.exchange_rate) return <span className="tw-text-slate-400">—</span>;
  return (
    <span>
      {formatThousands(rate.exchange_rate)}{' '}
      <span className="tw-text-slate-400">
        ({SOURCE_LABEL[rate.rate_source] || rate.rate_source}{rate.rate_date ? ` ${formatDate(rate.rate_date)}` : ''})
      </span>
    </span>
  );
};

RateCell.propTypes = { rate: PropTypes.object };

const TH = 'tw-px-3 tw-py-2 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500';
const TD = 'tw-px-3 tw-py-2 tw-text-[12.5px] tw-text-slate-700 tw-align-top';

const PaymentPlanRatesTable = ({ rows, selected, onToggle, onToggleAll, allSelected }) => {
  if (rows.length === 0) {
    return (
      <div className="tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-py-10 tw-text-center tw-text-sm tw-text-slate-500">
        Every non-USD milestone already has its USD rate.
      </div>
    );
  }

  return (
    <div className="tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-overflow-x-auto">
      <table className="tw-w-full tw-border-collapse">
        <thead className="tw-bg-slate-50 tw-border-0 tw-border-b tw-border-solid tw-border-slate-200">
          <tr>
            <th className={TH} style={{ width: 36 }}>
              <input type="checkbox" checked={allSelected} onChange={e => onToggleAll(e.target.checked)} aria-label="Select all" />
            </th>
            <th className={TH}>Project / Payment plan</th>
            <th className={TH}>Contract</th>
            <th className={TH}>Milestone</th>
            <th className={`${TH} tw-text-right`}>Amount</th>
            <th className={TH}>Current rate</th>
            <th className={TH}>New rate</th>
            <th className={`${TH} tw-text-right`}>USD</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const changes = row.changes.length ? row.changes : [null];
            return changes.map((change, i) => (
              <tr key={`${row.schedule_id}-${change?.id || i}`} className={i === 0 ? 'tw-border-0 tw-border-t tw-border-solid tw-border-slate-100' : ''}>
                {i === 0 && (
                  <>
                    <td className={TD} rowSpan={changes.length}>
                      <input type="checkbox" disabled={Boolean(row.error)} checked={selected.has(row.schedule_id)}
                        onChange={() => onToggle(row.schedule_id)} aria-label={`Select ${row.name}`} />
                    </td>
                    <td className={TD} rowSpan={changes.length}>
                      <div className="tw-font-semibold">{row.project_name || `Project #${row.project_id}`}</div>
                      <div className="tw-text-slate-500">
                        {row.name} <span className="tw-text-blue-600">{row.j_numbers.join(', ')}</span>
                      </div>
                      {row.released && (
                        <span className="tw-inline-block tw-mt-1 tw-px-1.5 tw-rounded tw-bg-emerald-50 tw-text-emerald-700 tw-text-[10px] tw-font-bold">
                          Released · RECEIVED UP TO will be recalculated
                        </span>
                      )}
                      {row.error && <div className="tw-text-red-600 tw-mt-1">{row.error}</div>}
                    </td>
                    <td className={TD} rowSpan={changes.length}>{row.contract_number || <span className="tw-text-slate-400">No contract</span>}</td>
                  </>
                )}
                {change ? (
                  <>
                    <td className={TD}>{change.name}</td>
                    <td className={`${TD} tw-text-right tw-whitespace-nowrap`}>{formatThousands(change.amount_received)} {change.currency}</td>
                    <td className={TD}><RateCell rate={change.from} /></td>
                    <td className={`${TD} tw-font-semibold`}><RateCell rate={change.to} /></td>
                    <td className={`${TD} tw-text-right tw-font-semibold tw-whitespace-nowrap`}>
                      {change.to?.amount_usd != null ? `${formatThousands(change.to.amount_usd)} USD` : '—'}
                    </td>
                  </>
                ) : <td className={TD} colSpan={5} />}
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
};

PaymentPlanRatesTable.propTypes = {
  rows: PropTypes.array.isRequired,
  selected: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
  onToggleAll: PropTypes.func.isRequired,
  allSelected: PropTypes.bool,
};

export default PaymentPlanRatesTable;
